import { Api } from '@/api/api.client';
import { Components } from '@/api/generated/api';
import { WHO_AM_I_QUERY_KEY } from '@/api/query.keys';
import { AVAILABLE_FRONTEND_BASE_LANGUAGES, AVAILABLE_FRONTEND_LOCALES, i18n } from '@/i18n/translations';
import { queryClient } from '@/plugins/vueQuery';
import { useAppConfigStore } from '@/stores/useAppConfigStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { useLocaleStore } from '@/stores/useLocaleStore';
import { useWhoAmIStore } from '@/stores/useWhoAmIStore';
import { getErrorTk } from '@/utils/error.utils';
import { getCurrentTenant } from '@/utils/role.utils';
import { DEFAULT_LANGUAGE } from 'common/constants/locale.constants';
import {
  getBaseLanguage,
  isLanguageCode,
  matchPreferredLanguage,
  normalizeLocaleTag,
  resolveEffectiveLocale,
} from 'common/utils/locale.utils';
import { ComputedRef, computed, ref, watch } from 'vue';
import { toast } from 'vue3-toastify';

// Module-singleton locale state. Active-language precedence (first wins):
// ?lang= URL override > disabled switch default > whoAmI.language > persisted store > navigator.languages > default.
// When switching is disabled by app config, interactive frontend visits use the default language.
interface ILocaleComposable {
  language: ComputedRef<string>;
  submittableLanguage: ComputedRef<string | undefined>;
  effectiveLocale: ComputedRef<string>;
  formattingLocale: ComputedRef<string>;
  canSwitchLanguage: ComputedRef<boolean>;
  setLanguage: (language: string) => Promise<boolean>;
}

interface ILocaleState {
  language: ComputedRef<string>;
  submittableLanguage: ComputedRef<string | undefined>;
  effectiveLocale: ComputedRef<string>;
  formattingLocale: ComputedRef<string>;
  canSwitchLanguage: ComputedRef<boolean>;
}

let state: ILocaleState | null = null;
let watcherRegistered = false;
// must be a ref: the computeds early-return on it, so a plain var would freeze them once watched
const urlPinnedLocale = ref<string | null>(null);
// Wording and number formatting follow the market a checkout is FOR, not the buyer's context: while
// the onboarding checkout is active this holds the school's country (CH → "CHF 96.00" even for a
// buyer without a tenant or with a German one). Null everywhere else.
const marketRegion = ref<string | null>(null);

export function setMarketRegion(isoCode: string | null): void {
  // strict shape: the tag flows into Intl APIs, which throw on garbage
  marketRegion.value = isoCode != null && /^[A-Z]{2}$/.test(isoCode) ? isoCode : null;
}

function readUrlLanguageOverride(): string | null {
  const raw = new URLSearchParams(window.location.search).get('lang');
  if (!raw) return null;
  const tag = normalizeLocaleTag(raw);
  // strict shape: the tag flows into Intl APIs, which throw on garbage
  if (!/^[a-z]{2,3}(-[A-Z]{2})?$/.test(tag)) return null;
  return AVAILABLE_FRONTEND_BASE_LANGUAGES.includes(getBaseLanguage(tag)) ? tag : null;
}

function getState(): ILocaleState {
  if (state) return state;

  const localeStore = useLocaleStore();
  const whoAmIStore = useWhoAmIStore();
  const appConfigStore = useAppConfigStore();

  const canSwitchLanguage = computed<boolean>(() => appConfigStore.languageSwitchEnabled);

  const language = computed<string>(() => {
    if (urlPinnedLocale.value) return getBaseLanguage(urlPinnedLocale.value);

    if (!canSwitchLanguage.value) return DEFAULT_LANGUAGE;

    // also covers `undefined` from a stale persisted whoAmI blob
    const serverLanguage = whoAmIStore.whoAmI?.language;
    if (serverLanguage != null && isLanguageCode(serverLanguage)) return serverLanguage;

    if (localeStore.persistedLanguage != null && isLanguageCode(localeStore.persistedLanguage)) {
      return localeStore.persistedLanguage;
    }

    const browserLanguages = (navigator.languages.length ? navigator.languages : [navigator.language]).filter(
      (candidate): candidate is string => !!candidate,
    );

    return (
      matchPreferredLanguage(browserLanguages, appConfigStore.selectableCodes ?? AVAILABLE_FRONTEND_BASE_LANGUAGES) ??
      DEFAULT_LANGUAGE
    );
  });

  // The language to persist/submit for the current user: the resolved language only while it is in the
  // current user-selectable set, otherwise undefined so callers omit it and the row falls back to the
  // default language at render time. The display `language` may be a code the user merely has persisted
  // or in the URL after a provider retired it (isUserSelectable=false) — submitting that hits the
  // backend's assertSelectableLanguage rejection, so it must not reach a write. selectableCodes==null
  // (app-config not yet loaded) trusts the resolved value, matching prior behavior; the backend stays
  // the enforcer.
  const submittableLanguage = computed<string | undefined>(() => {
    if (!canSwitchLanguage.value) return DEFAULT_LANGUAGE;

    const selectable = appConfigStore.selectableCodes;
    if (selectable == null) return language.value;
    return selectable.includes(language.value) ? language.value : undefined;
  });

  const tenantCountryIso = computed<string | null>(
    () => getCurrentTenant(whoAmIStore.whoAmI ?? undefined)?.countryIsoCode ?? null,
  );

  const effectiveLocale = computed<string>(() => {
    if (urlPinnedLocale.value) return urlPinnedLocale.value;
    const region = marketRegion.value ?? tenantCountryIso.value;
    return resolveEffectiveLocale(language.value, region, AVAILABLE_FRONTEND_LOCALES);
  });

  const formattingLocale = computed<string>(() => {
    // The market region outranks the tenant country AND a ?lang pin: the pin picks the language,
    // the market decides the number conventions (?lang=fr on a Swiss checkout → fr-CH).
    if (marketRegion.value) return `${language.value}-${marketRegion.value}`;
    if (urlPinnedLocale.value) return urlPinnedLocale.value;
    return tenantCountryIso.value ? `${language.value}-${tenantCountryIso.value}` : language.value;
  });

  state = { language, submittableLanguage, effectiveLocale, formattingLocale, canSwitchLanguage };
  return state;
}

function applyLocale(): void {
  const { effectiveLocale } = getState();
  i18n.global.locale.value = effectiveLocale.value;
  document.documentElement.setAttribute('lang', effectiveLocale.value);
  document.documentElement.setAttribute('dir', 'ltr'); // seam for future RTL
}

// Called once from main.ts after registerPlugins() (Pinia active) and before mount.
export function initLocale(): void {
  urlPinnedLocale.value = readUrlLanguageOverride();
  const { effectiveLocale } = getState();

  if (!watcherRegistered) {
    watcherRegistered = true;
    // one trigger for tenant switch, logout (whoAmI reset) and store changes
    watch(effectiveLocale, () => applyLocale());
  }

  applyLocale();
}

// Optimistic: a failed profile save keeps the local choice; returns false on failure.
async function setLanguage(language: string): Promise<boolean> {
  const localeStore = useLocaleStore();
  const authStore = useAuthStore();

  // No-op where switching is disabled: keeps the dead-code switcher from ever persisting a non-default language.
  if (!getState().canSwitchLanguage.value) return false;

  urlPinnedLocale.value = null;
  localeStore.setPersistedLanguage(language);
  applyLocale();

  if (authStore.csrfToken != null) {
    try {
      await Api.Routes.User.update(null, { language });
      await queryClient.invalidateQueries({ queryKey: WHO_AM_I_QUERY_KEY });
    } catch (e) {
      toast.error(String(i18n.global.t(getErrorTk(e))));
      return false;
    }
  }
  return true;
}

// A null profile language (e.g. SSO account) adopts + persists the resolved language — but never a ?lang override.
export function syncLocaleFromWhoAmI(whoAmI: Components.Schemas.WhoAmIDataDto): void {
  const localeStore = useLocaleStore();

  if (whoAmI.language != null) {
    if (isLanguageCode(whoAmI.language) && localeStore.persistedLanguage !== whoAmI.language) {
      localeStore.setPersistedLanguage(whoAmI.language);
    }
    return;
  }

  if (urlPinnedLocale.value != null) return;

  if (useAppConfigStore().selectableCodes == null) return;

  // Only adopt a language the user may actually select; if the resolved one isn't (e.g. a retired code
  // still in localStorage), stay null — it renders as the default and avoids a PATCH the backend rejects
  // on every whoAmI fetch.
  const adopted = getState().submittableLanguage.value;
  if (adopted == null) return;
  localeStore.setPersistedLanguage(adopted);
  // fire-and-forget: on failure whoAmI.language stays null, so adoption retries on the next fetch
  void Api.Routes.User.update(null, { language: adopted }).catch(() => undefined);
}

// Non-reactive accessor for module-scope utils; falls back when Pinia isn't ready yet.
export function getFormattingLocale(): string {
  try {
    return getState().formattingLocale.value;
  } catch {
    return i18n.global.locale.value;
  }
}

export function useLocale(): ILocaleComposable {
  const { language, submittableLanguage, effectiveLocale, formattingLocale, canSwitchLanguage } = getState();
  return { language, submittableLanguage, effectiveLocale, formattingLocale, canSwitchLanguage, setLanguage };
}
